// Full Privacy Policy viewer.
//
// Renders all PRIVACY_SECTIONS expanded (no accordion) — DPDP Act
// transparency principle: full disclosure up-front, not progressive.
// Supports embedded mode (in a page) and modal mode (from signup link).
//
// Inline **bold** markdown in section bodies is converted to <strong>;
// kept simple to avoid pulling in a markdown library.

import React, { useContext } from "react";
import Ctx from "../../contexts/AppContext";
import { sp, rad } from "../../constants/styles";
import {
    PRIVACY_VERSION,
    PRIVACY_EFFECTIVE_DATE,
    PRIVACY_SECTIONS,
    COMPANY_NAME,
} from "../../utils/privacyPolicy";

// Tiny **bold** renderer — splits on the **...** marker and wraps the
// captured group in <strong>. Anything more complex would warrant a
// real markdown library.
function renderInline(text) {
    const parts = text.split(/\*\*(.+?)\*\*/g);
    return parts.map((part, i) =>
        i % 2 === 1
            ? <strong key={i}>{part}</strong>
            : <React.Fragment key={i}>{part}</React.Fragment>
    );
}

export default function PrivacyView({ onClose, embedded = false }) {
    const { h, t } = useContext(Ctx);

    const content = (
        <div style={{ padding: embedded ? 0 : sp.lg, color: h.tx, fontSize: 13, lineHeight: 1.7 }}>
            <div style={{ textAlign: "center", marginBottom: sp.lg, paddingBottom: sp.md, borderBottom: `1px solid ${h.bd}` }}>
                <h1 style={{ color: h.tx, fontSize: 22, fontWeight: 700, margin: 0, letterSpacing: "-0.3px" }}>
                    Privacy Policy
                </h1>
                <div style={{ color: h.sc, fontSize: 12, marginTop: 6 }}>
                    {COMPANY_NAME} • Version {PRIVACY_VERSION} • Effective {PRIVACY_EFFECTIVE_DATE}
                </div>
                <div style={{ color: h.sc, fontSize: 11, marginTop: 4, fontStyle: "italic" }}>
                    Compliant with the Digital Personal Data Protection Act, 2023
                </div>
            </div>

            {PRIVACY_SECTIONS.map(section => (
                <div key={section.id} style={{ marginBottom: sp.lg }}>
                    <h2 style={{ color: h.tx, fontSize: 15, fontWeight: 700, margin: "0 0 8px", letterSpacing: "-0.1px" }}>
                        {section.title}
                    </h2>
                    {section.body.map((para, i) => (
                        <p key={i} style={{ color: h.tx, fontSize: 13, lineHeight: 1.7, margin: "0 0 10px" }}>
                            {renderInline(para)}
                        </p>
                    ))}
                </div>
            ))}

            <div style={{
                marginTop: sp.xl, padding: sp.md, borderRadius: rad.sm,
                background: `${h.ac}08`, border: `1px solid ${h.ac}30`,
                fontSize: 11, color: h.sc, lineHeight: 1.6
            }}>
                Last reviewed: {PRIVACY_EFFECTIVE_DATE}. By continuing to use the
                Service after a material update, you accept the current version
                of this Policy.
            </div>
        </div>
    );

    if (embedded) return content;

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                zIndex: 1000, display: "flex", alignItems: "center",
                justifyContent: "center", padding: sp.md
            }}>
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: h.bg, borderRadius: rad.lg, width: "100%",
                    maxWidth: 600, maxHeight: "90vh", overflow: "hidden",
                    display: "flex", flexDirection: "column",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
                }}>
                <div style={{
                    padding: "12px 16px", borderBottom: `1px solid ${h.bd}`,
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center", flexShrink: 0
                }}>
                    <strong style={{ color: h.tx, fontSize: 14 }}>Privacy Policy</strong>
                    <button
                        onClick={onClose}
                        style={{
                            background: `${h.bd}50`, border: "none", width: 28,
                            height: 28, borderRadius: 14, color: h.tx, cursor: "pointer",
                            fontWeight: 700, display: "flex", alignItems: "center",
                            justifyContent: "center"
                        }}>✕</button>
                </div>
                <div style={{ overflow: "auto", flex: 1 }}>
                    {content}
                </div>
                {onClose && (
                    <div style={{
                        padding: sp.md, borderTop: `1px solid ${h.bd}`,
                        flexShrink: 0, display: "flex", justifyContent: "flex-end"
                    }}>
                        <button
                            onClick={onClose}
                            style={{
                                background: h.ac, color: "#fff", border: "none",
                                padding: "10px 20px", borderRadius: rad.sm,
                                fontSize: 13, fontWeight: 600, cursor: "pointer"
                            }}>{t.close || "Close"}</button>
                    </div>
                )}
            </div>
        </div>
    );
}
